/**
 * SimpleInteractiveRenderer - Minimal HTML content renderer
 *
 * A barebones component that renders HTML content in an iframe.
 * No communication layer, no APIs - just pure HTML rendering.
 * Designed to grow vertically and allow page scrolling.
 */

import React, { useRef, useEffect, useState } from 'react';

interface SimpleInteractiveRendererProps {
  /** Raw HTML content/blob to render directly */
  htmlBlob?: string;
  /** URL source for iframe content */
  htmlSrc?: string | null;
  /** Additional CSS classes */
  className?: string;
  /** Custom styling */
  style?: React.CSSProperties;
  /** Minimum height for the iframe */
  minHeight?: number;
}

const SimpleInteractiveRenderer: React.FC<SimpleInteractiveRendererProps> = ({
  htmlBlob,
  htmlSrc,
  className = '',
  style = {},
  minHeight = 400,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(minHeight);

  // Create complete HTML document from blob content
  const createFullHTML = (content: string): string => {
    if (!content) return '';

    // If content is already a complete HTML document
    if (content.includes('<html') || content.includes('<!DOCTYPE')) {
      return content;
    }

    // HTML fragment - wrap in basic document structure
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Interactive Content</title>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: auto;
    }
    body {
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #ffffff;
      color: #333333;
      line-height: 1.5;
    }
    .content-container {
      width: 100%;
    }
    /* Ensure images and media are responsive */
    img, video, iframe {
      max-width: 100%;
      height: auto;
    }
  </style>
</head>
<body>
  <div class="content-container">
    ${content}
  </div>
</body>
</html>
    `;
  };

  // Auto-resize iframe to fit content
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let resizeObserver: ResizeObserver | null = null;

    const measureHeight = (iframeDocument: Document): number => {
      const body = iframeDocument.body;
      const html = iframeDocument.documentElement;

      return Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0,
        minHeight
      );
    };

    const handleLoad = () => {
      try {
        const iframeDocument =
          iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDocument) {
          // Check if the activity uses viewport-based sizing (100vh).
          // These activities are designed to fill a fixed viewport and should
          // NOT have their height overridden — instead we give them 720px
          // (the design target height).
          const rawHTML =
            iframeDocument.documentElement?.getAttribute('style') || '';
          const rawBody = iframeDocument.body?.getAttribute('style') || '';
          const styleSheets = iframeDocument.querySelectorAll('style');
          let usesViewportHeight = false;
          styleSheets.forEach((s) => {
            if (s.textContent && /100vh/.test(s.textContent)) {
              usesViewportHeight = true;
            }
          });
          if (/100vh/.test(rawHTML) || /100vh/.test(rawBody)) {
            usesViewportHeight = true;
          }

          if (usesViewportHeight) {
            // Activity is designed for a fixed viewport — give it 720px
            // (the desktop design target) and don't override its styles.
            setIframeHeight(720);
            return;
          }

          // For non-viewport activities, override height to auto so the
          // iframe grows to fit content naturally.
          const overrideStyle = iframeDocument.createElement('style');
          overrideStyle.textContent = `
            html, body {
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
            }
          `;
          iframeDocument.head.appendChild(overrideStyle);

          // Clean up any existing observer
          if (resizeObserver) {
            resizeObserver.disconnect();
          }

          // Create new observer for dynamic content changes
          resizeObserver = new ResizeObserver(() => {
            try {
              setIframeHeight(measureHeight(iframeDocument));
            } catch (error) {
              setIframeHeight(minHeight);
            }
          });

          // Start observing body for size changes
          resizeObserver.observe(iframeDocument.body);

          // Initial height calculation
          setIframeHeight(measureHeight(iframeDocument));
        }
      } catch (error) {
        // Cross-origin restrictions or other errors - use minimum height
        setIframeHeight(minHeight);
      }
    };

    iframe.addEventListener('load', handleLoad);

    // If already loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      handleLoad();
    }

    // Cleanup function
    return () => {
      iframe.removeEventListener('load', handleLoad);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [htmlBlob, htmlSrc, minHeight]);

  // Validate content - prioritize htmlSrc over htmlBlob
  if (!htmlSrc && !htmlBlob) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: '#666',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          minHeight: minHeight + 'px',
        }}
      >
        No content provided
      </div>
    );
  }

  // Use srcdoc for blob content (same-origin, allows ResizeObserver access)
  // Use src only for external URLs (s3_url)
  const usesSrcdoc = !htmlSrc && htmlBlob;
  const iframeSrc = htmlSrc || undefined;
  const iframeSrcdoc = usesSrcdoc ? createFullHTML(htmlBlob!) : undefined;

  return (
    <div
      className={`simple-interactive-renderer ${className}`}
      style={{
        width: '100%',
        ...style,
      }}
    >
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        srcDoc={iframeSrcdoc}
        style={{
          width: '100%',
          height: iframeHeight + 'px',
          border: 'none',
          display: 'block',
        }}
        sandbox="allow-scripts allow-same-origin allow-forms"
        title="Interactive Content"
        allow="accelerometer; camera; encrypted-media; gyroscope; microphone"
      />
    </div>
  );
};

export default SimpleInteractiveRenderer;
